import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useModal } from 'src/hooks/modalHooks';
import { isModalComplete } from 'src/components/ModalDialog';
import { HMITemplate, fetchHmiConfigurations } from 'src/hmi';
import HmiTemplateEditorModal from './HmiTemplateEditorModal';
import HmiConfigurationDeleteModal from './HmiConfigurationDeleteModal';

export interface HmiConfigurationTabProps {
  twinId?: string;
  solutionId?: string;
  siteId?: string;
  modelId?: string;
  canEdit?: boolean;
}

/**
 * Solutions &gt; Asset Hierarchy &gt; "HMI Configuration" tab.
 *
 * Per-twin authoring surface for the server-driven HMI template that backs
 * the sibling "HMI" tab. This replaces the earlier Settings &gt; HMI
 * Templates POC page: it is now scoped to the currently selected Digital
 * Twin, and - unlike the earlier page - each twin may own at most one
 * template, so there is no "duplicate" action and "Create Template" is
 * hidden once a template already exists for this twin.
 *
 * Persistence is the real HMI Configuration service (see `src/hmi/api.ts`),
 * addressed by `solutionId` / `siteId` / `twinId` - no separate id to track.
 */
function HmiConfigurationTab({ twinId, solutionId, siteId, modelId, canEdit }: HmiConfigurationTabProps) {
  const [template, setTemplate] = useState<HMITemplate | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [editing, setEditing] = useState<HMITemplate | undefined>(undefined);

  const refresh = () => {
    if (!twinId || !solutionId || !siteId) {
      setTemplate(undefined);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(undefined);
    fetchHmiConfigurations(solutionId, siteId, twinId)
      .then((all) => setTemplate(all[0]))
      .catch(() => setError('Failed to load the HMI configuration for this asset.'))
      .finally(() => setLoading(false));
  };

  useEffect(refresh, [twinId, solutionId, siteId]);

  const [DeleteDialog, showDeleteDialog] = useModal(HmiConfigurationDeleteModal, {
    template,
    solutionId,
    siteId,
    twinId,
    onClose: (event: unknown, reason: any) => {
      if (isModalComplete(reason)) refresh();
    },
  });

  const handleCreate = () => {
    setEditing({
      id: `tmpl-${Date.now()}`,
      name: 'New HMI Template',
      description: '',
      version: 1,
      modelIds: modelId ? [modelId] : [],
      pollIntervalSec: 7,
      screens: [
        { key: 'dashboard', label: 'Dashboard' },
        { key: 'settings', label: 'Settings' },
        { key: 'process', label: 'Process' },
        { key: 'log', label: 'Log' },
      ],
      groups: [],
      fields: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
  };

  const handleSaved = () => {
    setEditing(undefined);
    refresh();
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        The template configured here drives the &quot;HMI&quot; tab for this
        Digital Twin. Each twin can have only one HMI template.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!twinId ? (
        <Typography variant="body2" color="text.secondary">
          Select a Digital Twin in the asset tree to configure its HMI
          template.
        </Typography>
      ) : (
        <>
          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
            {!loading && !template && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                disabled={!canEdit}
                onClick={handleCreate}
              >
                Create Template
              </Button>
            )}
          </Stack>

          <Paper variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Version</TableCell>
                  <TableCell>Poll interval</TableCell>
                  <TableCell>Fields</TableCell>
                  <TableCell>Updated</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading && !template && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No HMI template configured yet. Click &quot;Create
                        Template&quot; to create one for this asset.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {template && (
                  <TableRow hover>
                    <TableCell>{template.name}</TableCell>
                    <TableCell>v{template.version}</TableCell>
                    <TableCell>{template.pollIntervalSec}s</TableCell>
                    <TableCell>{template.fields.length}</TableCell>
                    <TableCell>
                      {new Date(template.updatedAt).toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => setEditing(template)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        disabled={!canEdit}
                        onClick={() => showDeleteDialog()}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}

      {editing !== undefined && twinId && solutionId && siteId && (
        <HmiTemplateEditorModal
          template={editing}
          readOnly={!canEdit}
          isNew={!template}
          solutionId={solutionId}
          siteId={siteId}
          twinId={twinId}
          onClose={() => setEditing(undefined)}
          onSaved={handleSaved}
        />
      )}
      {DeleteDialog}
    </Box>
  );
}

export default HmiConfigurationTab;

